import {omit, pick, whenIdle} from '@servicenow/ui-internal';
import {configConstants, getConfigItem} from '@servicenow/ui-config';
import {getUserSysId, makePersistentCache, makeLRUCachePolicy} from '@servicenow/ui-effect-helpers';
import createSnHttp from './snHttp';
import filterHeaders from './filterHeaders';
import log from './log';

const IGNORED_HEADERS = ['NOW-UI-INTERACTION', 'NOW-UX-INTERACTION'];
const {HTTP_CACHING_ENABLED, HTTP_CACHE_CAPACITY, HTTP_CACHE_MAX_PERSIST} = configConstants;
const isCacheEnabled = getConfigItem(HTTP_CACHING_ENABLED);
const cacheCapacity = getConfigItem(HTTP_CACHE_CAPACITY);
const cacheMaxPersist = getConfigItem(HTTP_CACHE_MAX_PERSIST) || 2880;

let persistantCache;

const getCache = async function () {
	if (persistantCache) {
		return persistantCache;
	}
	try {
		persistantCache = await makePersistentCache({
			dbName: 'Seismic',
			storeName: 'gqlQueries',
			schemaVersion: 1,
			options: {cachePolicy: makeLRUCachePolicy(cacheCapacity)}
		});
	} catch (e) {
		return null;
	}
	return persistantCache;
};

const getCacheKey = ({url, headers, params}) => {
	const stringifiedHeaders = JSON.stringify(omit(headers, IGNORED_HEADERS));
	const stringifiedParams = JSON.stringify(params);

	// @FIXME: This is more fragile than a faberge egg.  Find or create a public API to get a user sys_id
	const userToken = getUserSysId(globalThis);

	return `${url}:${stringifiedHeaders}:${stringifiedParams}:${userToken}`;
};

export default async function sendRequest(url, method, options) {
	const snHttp = window?.nowUiFramework?.snHttp || (await createSnHttp());
	const {
		headers = {},
		params,
		data,
		onUploadProgress = () => {},
		batch = true,
		cacheable = false,
		cancelPromiseSymbol,
		multipartConfig,
		span: activeSpan
	} = options;

	const abortController = new AbortController();
	const updatedHeaders = filterHeaders(headers);
	const cacheKey = getCacheKey({url, headers: updatedHeaders, params});
	// Disable caching for multipart requests
	// eslint-disable-next-line eqeqeq
	const cacheRequested = cacheable && multipartConfig == null;
	const shouldCache = cacheRequested && method === 'GET';
	const isImpersonating = window.NOW?.user?.isImpersonating;
	const promise = () => {
		return new Promise((resolve, reject) => {
			snHttp
				.request(url, method, {
					headers: updatedHeaders,
					params,
					data,
					onUploadProgress,
					batch,
					signal: abortController.signal,
					multipartConfig,
					span: activeSpan
				})
				.then(async results => {
					if (isCacheEnabled && shouldCache && !isImpersonating) {
						const cache = await getCache();
						if (cache) {
							const dataToCache = pick(results, ['data', 'headers', 'status', 'statusText']);
							// add now as the cached date if its not on the header
							if (!dataToCache?.headers?.date) {
								if (dataToCache?.headers) {
									dataToCache.headers.date = Date.now();
								}
							}
							await cache.set(cacheKey, dataToCache);

							whenIdle(() => cache.persist());
						}
					}
					activeSpan && activeSpan.setStatus(results.status);
					resolve(results);
				})
				.catch(error => {
					const {response: {data, status, statusText, headers} = {}, message} = error;
					if (activeSpan) {
						activeSpan.setStatus(status);
						activeSpan.recordException(error);
					}

					reject({
						data,
						status,
						statusText,
						options: {
							...(updatedHeaders && {headers: updatedHeaders}),
							...(headers && {responseHeaders: headers}),
							...(params && {params}),
							...(data && {data})
						},
						...(message && {message})
					});
				});
		});
	};

	if (cancelPromiseSymbol) {
		promise[cancelPromiseSymbol] = () => {
			abortController.abort();
		};
	}
	const cache = await getCache();
	const isCached = cache ? await cache.has(cacheKey) : false;
	if (shouldCache && isCached) {
		const cachedResult = await cache.get(cacheKey);

		const {headers} = cachedResult;
		const cachedDate = new Date(headers?.date);
		if (headers?.date && cachedDate) {
			const minutesSinceCached = (Date.now() - cachedDate) / 1000 / 60;
			if (minutesSinceCached < cacheMaxPersist) {
				return {
					isCachedResult: true,
					refetchPromise: promise,
					...cachedResult
				};
			}
		}
	} else if (cacheRequested && !shouldCache) {
		log(`HTTP caching not available for ${method} method requests`);
	}

	return {
		isCachedResult: false,
		requestPromise: promise
	};
}
